/**
 * R4 Negative Consequence Features in MMCTs.
 *
 * Michael Wallace's Redlining template (2026-10-18): institution level, one small table per feature,
 *   Feature | Minority Concentration | {feature} | Applications | % | Diff. | Sig.
 * with a Non-MMCT row and an MMCT row. Same layout and colours as the Word export (fldMmctBlocks.ts):
 * Diff. red for a disparity toward MMCTs, green (led by a negative sign) for none, black when equal;
 * Sig. red for <1% / <5%, orange for <10%, green otherwise.
 */

import clsx from 'clsx';

interface R4FeatureRow {
  feature: string;
  featureLabel: string;
  totalCount: number;
  totalApps: number;
  totalPct: number;
  nmctCount: number;
  nmctTotal: number;
  nmctPct: number;
  mmctCount: number;
  mmctTotal: number;
  mmctPct: number;
  difference: number;
  hasDisparity: boolean;
  pValue: number | null;
  testUsed: string;
  significanceLevel: string;
  riskLevel: string;
}

interface R4MatrixProps {
  rows: R4FeatureRow[];
}

/** The template's column heading for each feature. */
const FEATURE_COLUMN: Record<string, string> = {
  INTEREST_ONLY: 'Interest-Only Payments',
  NEG_AM: 'Negative Amortization',
  REVERSE_MORTGAGE: 'Reverse Mortgage',
  OTHER_NON_AM: 'Other Non-Am. Features',
  BALLOON: 'Balloon Payment',
  HOEPA: 'High-Cost Mortgage',
  PREPAYMENT_PENALTY: 'Prepayment Pen. Term',
  INTRO_RATE_PERIOD: 'Introductory Rate Period',
};

const pct = (count: number, total: number) => (total > 0 ? `${((count / total) * 100).toFixed(2)}%` : 'NA');

export function R4Matrix({ rows }: R4MatrixProps) {
  if (rows.length === 0) {
    return <p className="text-sm text-ink-3">No negative-consequence feature data for this LAR.</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold text-ink">Institution</p>
      {rows.map((r) => {
        const comparable = r.nmctTotal > 0 && r.mmctTotal > 0;
        const diff = comparable ? (r.mmctCount / r.mmctTotal - r.nmctCount / r.nmctTotal) * 100 : null;
        const shownDiff = diff === null ? 'NA' : diff.toFixed(2);
        const tested = comparable && shownDiff !== '0.00' && shownDiff !== '-0.00';
        const sig = comparable ? r.significanceLevel ?? 'NA' : 'NA';
        const sigClass = !tested
          ? undefined
          : sig === '<1%' || sig === '<5%' ? 'text-red-700'
          : sig === '<10%' ? 'text-orange-700'
          : 'text-emerald-700';
        const headers = ['Feature', 'Minority Concentration', FEATURE_COLUMN[r.feature] ?? r.featureLabel, 'Applications', '%', 'Diff.', 'Sig.'];
        return (
          <table key={r.feature} className="min-w-full text-sm border border-rule">
            <thead className="bg-panel-2 text-ink-2">
              <tr>
                {headers.map((h, i) => (
                  <th key={h} className={clsx('px-3 py-2 font-medium border-b border-rule', i < 2 ? 'text-left' : 'text-center')}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="tabular-nums">
              <tr className="border-b border-rule">
                <td rowSpan={2} className="px-3 py-2 align-top font-medium text-ink">{r.featureLabel}</td>
                <td className="px-3 py-2">Non-MMCT</td>
                <td className="px-3 py-2 text-center">{r.nmctCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-center">{r.nmctTotal.toLocaleString()}</td>
                <td className="px-3 py-2 text-center">{pct(r.nmctCount, r.nmctTotal)}</td>
                <td className="px-3 py-2 text-center">-</td>
                <td className="px-3 py-2 text-center">-</td>
              </tr>
              <tr>
                <td className="px-3 py-2">MMCT</td>
                <td className="px-3 py-2 text-center">{r.mmctCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-center">{r.mmctTotal.toLocaleString()}</td>
                <td className="px-3 py-2 text-center">{pct(r.mmctCount, r.mmctTotal)}</td>
                <td className={clsx('px-3 py-2 text-center', tested && 'font-semibold', tested && (r.hasDisparity ? 'text-red-700' : 'text-emerald-700'))}>
                  {shownDiff}
                </td>
                <td className={clsx('px-3 py-2 text-center', tested && 'font-semibold', sigClass)}>{sig}</td>
              </tr>
            </tbody>
          </table>
        );
      })}
    </div>
  );
}
